// Polymarket vault actions. The derive flow is genuinely round-trippy —
// the browser signs an EIP-712 challenge, the executor relays the POST to
// PM, the PM-returned creds come back via WS as a CustomEvent so they can
// be sealed under the vault's master key without ever entering the
// Zustand store.

import type { NormalizedOrder, PolymarketVenueAuth, TIF } from '@rfqsports/shared';
import {
  buildPolymarketL1,
  getPolymarketAddress,
  setPolymarketL2Creds,
  signPolymarketRequest,
  withPolymarketAccount,
} from './index.js';
import { signPolymarketOrder } from '../sign/polymarket-order.js';
import { send } from '../ws-client.js';
import { useStore } from '../store.js';

const TEST_PATH_BASE = '/balance-allowance';
const DERIVE_TIMEOUT_MS = 15_000;

interface L2ResultMessage {
  t: 'vault_polymarket_l2_result';
  ref: string;
  ok: boolean;
  apiKey?: string;
  secret?: string;
  passphrase?: string;
  reason?: string;
}

export async function derivePolymarketL2(): Promise<{ ok: boolean; reason?: string }> {
  const challenge = await buildPolymarketL1();
  if (!challenge) return { ok: false, reason: 'polymarket key missing from vault' };
  const ref = crypto.randomUUID();

  const ackPromise = waitForDeriveResult(ref);
  useStore.getState().startPolymarketDerive();
  const sent = send({
    t: 'vault_derive_polymarket_l2',
    ref,
    address: challenge.address,
    signature: challenge.signature,
    timestamp: challenge.timestamp,
    nonce: challenge.nonce,
  });
  if (!sent) {
    useStore.getState().setPolymarketDeriveResult({ ok: false, reason: 'executor not connected' });
    return { ok: false, reason: 'executor not connected' };
  }
  return ackPromise;
}

function waitForDeriveResult(ref: string): Promise<{ ok: boolean; reason?: string }> {
  return new Promise((resolve) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      document.removeEventListener('ladderbook:polymarket-l2-result', listener);
      useStore.getState().setPolymarketDeriveResult({ ok: false, reason: 'derive timed out' });
      resolve({ ok: false, reason: 'derive timed out' });
    }, DERIVE_TIMEOUT_MS);

    const listener = async (e: Event): Promise<void> => {
      const msg = (e as CustomEvent<L2ResultMessage>).detail;
      if (!msg || msg.t !== 'vault_polymarket_l2_result') return;
      if (msg.ref !== ref) return;
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('ladderbook:polymarket-l2-result', listener);
      if (msg.ok && msg.apiKey && msg.secret && msg.passphrase) {
        try {
          await setPolymarketL2Creds({
            apiKey: msg.apiKey,
            secret: msg.secret,
            passphrase: msg.passphrase,
          });
          useStore.getState().setPolymarketDeriveResult({ ok: true });
          resolve({ ok: true });
        } catch (err) {
          const reason = String(err);
          useStore.getState().setPolymarketDeriveResult({ ok: false, reason });
          resolve({ ok: false, reason });
        }
      } else {
        const reason = msg.reason ?? 'unknown derive failure';
        useStore.getState().setPolymarketDeriveResult({ ok: false, reason });
        resolve({ ok: false, reason });
      }
    };
    document.addEventListener('ladderbook:polymarket-l2-result', listener);
  });
}

// Constructs everything the executor needs to submit a Polymarket order:
// the EIP-712-signed order struct, the canonical POST body, and the L2
// HMAC headers binding to that body. Caller embeds the result in a
// submit_order wire message.
export async function buildPolymarketSubmitAuth(
  order: NormalizedOrder,
  tif: TIF,
): Promise<{ auth?: PolymarketVenueAuth; reason?: string }> {
  const vault = useStore.getState().vault;
  if (!vault.hasPolymarketL2) {
    return { reason: 'derive L2 credentials in /profile first' };
  }
  // PM only supports GTC/IOC/FOK natively. Map our TIF onto theirs.
  const orderType = (tif === 'FOK' ? 'FOK' : tif === 'IOC' ? 'IOC' : 'GTC') as 'GTC' | 'IOC' | 'FOK';

  const signed = await withPolymarketAccount(async (account) => {
    return signPolymarketOrder(account, {
      tokenId: order.outcomeId,
      price: order.price,
      size: order.size,
      side: order.side,
      ...(orderType !== 'GTC' ? { expiration: Math.floor(Date.now() / 1000) } : {}),
    });
  });
  if (!signed) return { reason: 'polymarket vault not unlocked' };

  // Read the L2 apiKey by signing a dummy probe and pulling it out of the
  // returned header. Cheaper: we know the apiKey because signPolymarketL2
  // returns it; but we need it for the body. signPolymarketRequest gives
  // us the headers — same shape includes apiKey.
  const probe = await signPolymarketRequest('POST', '/order', '');
  if (!probe) return { reason: 'polymarket L2 missing' };
  const apiKey = probe.apiKey;

  const body = JSON.stringify({ order: signed, owner: apiKey, orderType });
  // Re-sign with the actual body now that we know it.
  const headers = await signPolymarketRequest('POST', '/order', body);
  if (!headers) return { reason: 'polymarket L2 missing' };

  return {
    auth: {
      venue: 'polymarket',
      apiKey: headers.apiKey,
      apiSignature: headers.signature,
      apiPassphrase: headers.passphrase,
      apiTimestamp: headers.timestamp,
      body,
    },
  };
}

// Build a signed cancel envelope. `venueOrderId` is the PM-returned id
// from the original ack.
export async function buildPolymarketCancelAuth(
  venueOrderId: string,
): Promise<{ auth?: PolymarketVenueAuth; reason?: string }> {
  const body = JSON.stringify({ orderID: venueOrderId });
  const headers = await signPolymarketRequest('POST', '/order/cancel', body);
  if (!headers) return { reason: 'polymarket L2 missing' };
  return {
    auth: {
      venue: 'polymarket',
      apiKey: headers.apiKey,
      apiSignature: headers.signature,
      apiPassphrase: headers.passphrase,
      apiTimestamp: headers.timestamp,
      body,
    },
  };
}

export async function refreshPolymarketUser(): Promise<{ ok: boolean; reason?: string }> {
  const address = getPolymarketAddress();
  if (!address) return { ok: false, reason: 'polymarket key missing' };
  const positionsPath = `/positions?user=${encodeURIComponent(address)}`;
  const [positions, orders] = await Promise.all([
    signPolymarketRequest('GET', positionsPath, ''),
    signPolymarketRequest('GET', '/orders', ''),
  ]);
  if (!positions || !orders) {
    return { ok: false, reason: 'derive L2 credentials first' };
  }
  const ok = send({
    t: 'refresh_user_polymarket',
    ref: crypto.randomUUID(),
    address,
    positions,
    orders,
  });
  return ok ? { ok: true } : { ok: false, reason: 'executor not connected' };
}

export async function cancelPolymarketOrderViaWs(args: {
  clientOrderId: string;
  venueOrderId: string;
  marketId: string;
}): Promise<{ ok: boolean; reason?: string }> {
  const built = await buildPolymarketCancelAuth(args.venueOrderId);
  if (!built.auth) return { ok: false, ...(built.reason ? { reason: built.reason } : {}) };
  const vaultId = useStore.getState().vault.vaultId;
  const ok = send({
    t: 'cancel_order',
    clientOrderId: args.clientOrderId,
    venue: 'polymarket',
    marketId: args.marketId,
    venueOrderId: args.venueOrderId,
    venueAuth: built.auth,
    ...(vaultId ? { vaultId } : {}),
  });
  return ok ? { ok: true } : { ok: false, reason: 'executor not connected' };
}

export async function testPolymarketBalance(): Promise<{ ok: boolean; reason?: string }> {
  const address = getPolymarketAddress();
  if (!address) return { ok: false, reason: 'polymarket key missing' };
  const path = `${TEST_PATH_BASE}?address=${encodeURIComponent(address)}`;
  const signed = await signPolymarketRequest('GET', path, '');
  if (!signed) return { ok: false, reason: 'derive L2 credentials first' };
  useStore.getState().startPolymarketTest();
  const ok = send({
    t: 'vault_test_polymarket',
    ref: crypto.randomUUID(),
    address,
    headers: signed,
  });
  if (!ok) {
    useStore.getState().setPolymarketTestResult({ ok: false, reason: 'executor not connected' });
    return { ok: false, reason: 'executor not connected' };
  }
  return { ok: true };
}
